import React, { useEffect, useRef, useState } from 'react';
import { Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { useNotifications } from 'app/store/contexts/NotificationsContext';

// Same map the sidebar uses so the toast renders with the matching channel
// branding (icon + accent color + label).
const CHANNEL_META = {
	whatsapp:      { Icon: WhatsAppIcon,  color: '#25D366', label: 'WhatsApp' },
	facebook:      { Icon: FacebookIcon,  color: '#1877F2', label: 'Facebook' },
	facebook_post: { Icon: FacebookIcon,  color: '#1877F2', label: 'Facebook' },
	messenger:     { Icon: ChatIcon,      color: '#0084FF', label: 'Messenger' },
	instagram:     { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram' },
	email:         { Icon: EmailIcon,     color: '#EA4335', label: 'Email' },
	sms:           { Icon: SmsIcon,       color: '#9CA3AF', label: 'SMS' }
};

const TOAST_LIFETIME_MS = 5000;   // visible time before fade-out starts
const TOAST_FADE_MS = 320;        // duration of the fade-out animation

const useStyles = makeStyles(theme => ({
	'@global': {
		'@keyframes notifToastSlideIn': {
			from: { opacity: 0, transform: 'translateX(40px) scale(0.96)' },
			to:   { opacity: 1, transform: 'translateX(0) scale(1)' }
		},
		'@keyframes notifToastFadeOut': {
			from: { opacity: 1, transform: 'translateX(0) scale(1)' },
			to:   { opacity: 0, transform: 'translateX(20px) scale(0.96)' }
		}
	},
	// Stack of toasts. Sits just below the toolbar at the right edge.
	stack: {
		position: 'fixed',
		top: '88px',
		right: theme.spacing(2.5),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.25),
		zIndex: 1450,
		pointerEvents: 'none',
		maxWidth: '95vw',
		'@media (max-width: 768px)': {
			top: '72px',
			right: theme.spacing(1)
		}
	},
	toast: {
		width: 360,
		maxWidth: '95vw',
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.25),
		padding: theme.spacing(1.25, 1.5),
		borderRadius: 14,
		background: theme.palette.background.paper,
		boxShadow: theme.palette.type === 'dark'
			? '0 18px 40px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.4)'
			: '0 18px 40px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.08)',
		border: `1px solid ${theme.palette.divider}`,
		cursor: 'pointer',
		pointerEvents: 'auto',
		transition: 'transform 0.18s ease, box-shadow 0.18s ease',
		animation: '$notifToastSlideIn 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) both',
		'&:hover': {
			transform: 'translateY(-1px)',
			boxShadow: theme.palette.type === 'dark'
				? '0 22px 48px rgba(0,0,0,0.6), 0 5px 14px rgba(0,0,0,0.45)'
				: '0 22px 48px rgba(0,0,0,0.18), 0 5px 14px rgba(0,0,0,0.10)'
		},
		'&:active': { transform: 'translateY(0) scale(0.99)' }
	},
	// Applied when the toast is on its way out. Replaces the slide-in
	// animation with a fade-out, then the parent removes the DOM node.
	toastLeaving: {
		animation: `$notifToastFadeOut ${TOAST_FADE_MS}ms ease-out forwards`,
		pointerEvents: 'none'
	},
	avatarWrap: { position: 'relative', flexShrink: 0 },
	avatar: {
		width: 44,
		height: 44,
		background: theme.palette.type === 'dark' ? '#374151' : '#E4E6EB',
		color: theme.palette.text.primary,
		fontWeight: 700,
		fontSize: 15
	},
	channelBadge: {
		position: 'absolute',
		right: -2,
		bottom: -2,
		width: 20,
		height: 20,
		borderRadius: '50%',
		background: theme.palette.background.paper,
		border: `2px solid ${theme.palette.background.paper}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: theme.palette.type === 'dark'
			? '0 1px 4px rgba(0,0,0,0.5)'
			: '0 1px 4px rgba(0,0,0,0.18)'
	},
	body: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
	title: {
		fontSize: 13.5,
		fontWeight: 700,
		color: theme.palette.text.primary,
		lineHeight: 1.35,
		wordBreak: 'break-word'
	},
	desc: {
		fontSize: 12.5,
		color: theme.palette.text.secondary,
		lineHeight: 1.4,
		wordBreak: 'break-word',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden'
	},
	meta: {
		marginTop: 2,
		fontSize: 11,
		fontWeight: 600,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		color: theme.palette.text.secondary
	},
	closeBtn: {
		width: 24,
		height: 24,
		padding: 0,
		color: theme.palette.text.secondary,
		opacity: 0.6,
		'&:hover': {
			opacity: 1,
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
		}
	}
}));

const getInitials = name => {
	if (!name) return '•';
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(p => p[0]?.toUpperCase())
		.join('');
};

// Single toast tile — owns two timers:
//   leaveTimer  → starts the fade-out animation after TOAST_LIFETIME_MS
//   removeTimer → calls onDismiss after the fade animation completes
// so the parent can keep the DOM node mounted for the full fade.
function ToastItem({ notification, onActivate, onDismiss }) {
	const classes = useStyles();
	const [leaving, setLeaving] = useState(false);
	const leaveTimerRef = useRef(null);
	const removeTimerRef = useRef(null);

	// Kick off the lifetime timer once on mount. We don't pause on hover —
	// the user asked for a strict 5-second visible time.
	useEffect(() => {
		leaveTimerRef.current = setTimeout(() => {
			setLeaving(true);
		}, TOAST_LIFETIME_MS);
		return () => {
			if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
			if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
		};
	}, []);

	// Once the leaving flag flips, schedule the actual removal from state
	// after the fade animation finishes.
	useEffect(() => {
		if (!leaving) return undefined;
		removeTimerRef.current = setTimeout(() => {
			onDismiss(notification.toastId);
		}, TOAST_FADE_MS);
		return () => {
			if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
		};
	}, [leaving, notification.toastId, onDismiss]);

	const meta = CHANNEL_META[(notification.channel_type || '').toLowerCase()] || null;
	const ChannelIcon = meta?.Icon || null;
	const channelColor = meta?.color || '#9CA3AF';
	const channelLabel =
		meta?.label || (notification.channel_type || '').replace(/^./, c => c.toUpperCase());
	const displayTitle =
		notification.title || notification.actor_name || channelLabel || 'Notification';

	// Trigger fade-out + then activate after removal. Activating immediately
	// is fine too, but flipping the visual class first makes the tile feel
	// "responsive" instead of instantly vanishing.
	const beginLeaving = () => {
		if (leaveTimerRef.current) {
			clearTimeout(leaveTimerRef.current);
			leaveTimerRef.current = null;
		}
		setLeaving(true);
	};

	const handleClick = e => {
		e.preventDefault();
		e.stopPropagation();
		beginLeaving();
		onActivate(notification);
	};

	const handleClose = e => {
		e.preventDefault();
		e.stopPropagation();
		beginLeaving();
	};

	return (
		<div
			className={`${classes.toast} ${leaving ? classes.toastLeaving : ''}`}
			role="button"
			tabIndex={0}
			onClick={handleClick}
		>
			<div className={classes.avatarWrap}>
				<Avatar
					src={notification.actor_profile_pic || undefined}
					className={classes.avatar}
				>
					{notification.actor_profile_pic
						? null
						: getInitials(notification.actor_name || channelLabel)}
				</Avatar>
				{ChannelIcon && (
					<span className={classes.channelBadge}>
						<ChannelIcon style={{ fontSize: 13, color: channelColor }} />
					</span>
				)}
			</div>
			<div className={classes.body}>
				<div className={classes.title}>{displayTitle}</div>
				{notification.description ? (
					<div className={classes.desc}>{notification.description}</div>
				) : null}
				<div className={classes.meta}>
					<span style={{ color: channelColor }}>{channelLabel}</span>
					<span>·</span>
					<span>Just now</span>
				</div>
			</div>
			<IconButton size="small" className={classes.closeBtn} onClick={handleClose}>
				<CloseIcon style={{ fontSize: 14 }} />
			</IconButton>
		</div>
	);
}

function NotificationToast() {
	const classes = useStyles();
	const { socketToasts, dismissToast, activateNotification } = useNotifications();

	if (!socketToasts || socketToasts.length === 0) return null;

	return (
		<div className={classes.stack} aria-live="polite" aria-atomic="false">
			{socketToasts.map(n => (
				<ToastItem
					key={n.toastId}
					notification={n}
					onActivate={activateNotification}
					onDismiss={dismissToast}
				/>
			))}
		</div>
	);
}

export default NotificationToast;
